<template>
  <div class="dark:text-white">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div class="flex items-center gap-3">
        <div class="bg-green-500/20 p-2 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-green-500">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
        </div>
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Device Whitelist
        </h1>
      </div>

      <button
        @click="showAddModal = true"
        class="flex items-center gap-2 btn bg-blue-600 text-white hover:bg-blue-700 transition-all"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        <span>Add MAC Address</span>
      </button>
    </div>

    <!-- Whitelist Table -->
    <GenericMivuLiteTable
      :columns="columns"
      :items="whitelistItems"
      itemKey="id"
      emptyMessage="No whitelisted devices found. Add a MAC address to get started."

    >
      <template #name="{value}">
        {{ formatValue("name", value) }}
      </template>
      <template #device_type="{ value }">
        {{ formatValue("device_type", value) }}
      </template>
      <template #registered_at="{value}">
        {{ formatValue("registered_at", value) }}
      </template>
      <template #is_online="{ value }">
        <span
          :class="[
            'px-3 py-1 rounded-full text-xs font-medium',
            value ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
          ]"
        >
          {{ value ? 'ONLINE' : 'OFFLINE' }}
        </span>
      </template>
      <template #actions="{ item }">
        <button
          @click="deleteDeviceWhitelist(item.id)"
          class="flex items-center gap-2 btn bg-red-600 text-white hover:bg-red-700 transition-all text-sm"
        >
          <Trash2/>
          Remove
        </button>
      </template>
    </GenericMivuLiteTable>


    <div v-if="showAddModal"
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="addingUser = false"
    >
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">

      <div>
        <h2 class="text-lg font-semibold mb-1">Whitelist New Device</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-xs uppercase tracking-widest mb-2">MAC Address</label>
            <input 
              type="text"
              v-model="newDeviceObject.mac_address"
              :class="[
                'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                fieldErrors['email']
                  ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              ]"
            >
          </div>

          <div>
            <label class="block text-xs uppercase tracking-widest mb-2">Description</label>
            <input 
              type="text"
              v-model="newDeviceObject.description"
              :class="[
                  'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                  fieldErrors['description']
                    ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                ]"
            >
          </div>
        </div>
      </div>

      <div>
        <div class="h-px bg-white/10 mb-5" />
        <div class="flex gap-3 justify-end">
          <button
            @click="closeAddModal"
            class="px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
          >
            Cancel
          </button>
          <button
            @click="addDeviceWhitelist"
            class="btn text-sm font-semibold hover:opacity-90 active:scale-95 transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>

    </div>
  </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import GenericMivuLiteTable from '@/components/GenericMivuLiteTable.vue';
import { getWhitelistItems, addToWhitelist, deleteWhitelistItem } from '@/services/mivulite/whitelistService';
import { Trash2, Trash2Icon } from 'lucide-vue-next';

const columns = [
  { key: 'mac_address', label: 'MAC Address' },
  { key: 'name', label: 'Description' },
  { key: 'device_type', label: 'Device Type' },
  { key: 'is_online', label: 'Status' },
  { key: 'registered_at', label: 'Date Added' },
  { key: 'actions', label: 'Actions' }
];

const whitelistItems = ref([]);
const showAddModal = ref(false);
const newDeviceObject = ref({
  mac_address: '',
  description: ''
})

const fieldErrors = ref('')

const fetchWhitelistItems = async () => {
  try {
    const response = await getWhitelistItems();
    whitelistItems.value = response["devices"]
  } catch (error) {
    console.error('Error fetching whitelist items:', error);
  }
};

const addDeviceWhitelist = async () => {
  if (newDeviceObject.value && newDeviceObject.value) {
    try {
      console.log(newDeviceObject.value)
      const response = await addToWhitelist(newDeviceObject.value);
      await fetchWhitelistItems();
      closeAddModal();
    } catch (error) {
      console.error('Error adding whitelist item:', error);
    }
  }
};

const closeAddModal = () => {
  showAddModal.value = false;
};


const deleteDeviceWhitelist = async (id) => {
  try {
    await deleteDeviceWhitelist(id);
    await fetchWhitelistItems();
  } catch (error) {
    console.error(error);
  }

};

const removeItem = async (id) => {
  if (confirm('Are you sure you want to remove this device from the whitelist?')) {
    try {
      await deleteWhitelistItem(id);
      await fetchWhitelistItems();
    } catch (error) {
      console.error('Error deleting whitelist item:', error);
    }
  }
};


const formatValue = (key, value) => {
  if (key === "registered_at" || key === "created_at") {
    return formatDateTime(value);
  }
  return value !== null && value !== undefined ? value : "N/A";
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  };
  return new Date(dateString).toLocaleString(undefined, options);
};

onMounted(() => {
  fetchWhitelistItems();
});
</script>